import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Shield, Layers, Users, FolderLock, Sparkles } from 'lucide-react';

import CrystalizedBall from '../components/landing/CrystalizedBall';
import GhostFibers from '../components/landing/GhostFibers';
import Orb from '../components/landing/Orb';
import LogoLoop from '../components/landing/LogoLoop';
import '../styles/landing-page.css';

/** Per-environment login background, set at build time: 'ball' (default), 'fibers' or 'orb'. */
const LOGIN_BACKGROUND = ['fibers', 'orb'].includes(process.env.REACT_APP_LOGIN_BACKGROUND) ? process.env.REACT_APP_LOGIN_BACKGROUND : 'ball';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/brand/prolense-logo.png`;

/** The ball needs WebGL2 and motion; without either the plain brand gradient shows. */
function useBallEnabled() {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        let webgl2 = false;
        try {
            webgl2 = !!document.createElement('canvas').getContext('webgl2');
        } catch {
            webgl2 = false;
        }
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => setEnabled(webgl2 && !reduced.matches);
        sync();
        reduced.addEventListener('change', sync);
        return () => reduced.removeEventListener('change', sync);
    }, []);
    return enabled;
}

/** A shader failure must never blank the sign-in page. */
class BackgroundBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    componentDidCatch(err) {
        console.warn('Login background disabled:', err);
    }
    render() {
        return this.state.failed ? null : this.props.children;
    }
}

/**
 * Public landing / sign-in — ProLense brand identity (navy + orange + silver) with LogoLoop frameworks strip.
 */
const LandingPage = ({ onEnter }) => {
    const ballEnabled = useBallEnabled();
    const frameworkLogos = useMemo(
        () => [
            { node: <span className="pl-landing__fw-pill">ISO 27001</span>, title: 'ISO 27001' },
            { node: <span className="pl-landing__fw-pill">SOC 2</span>, title: 'SOC 2' },
            { node: <span className="pl-landing__fw-pill">GDPR</span>, title: 'GDPR' },
            { node: <span className="pl-landing__fw-pill">PCI DSS</span>, title: 'PCI DSS' },
            { node: <span className="pl-landing__fw-pill">HIPAA</span>, title: 'HIPAA' },
            { node: <span className="pl-landing__fw-pill">NIST CSF</span>, title: 'NIST CSF' },
            { node: <span className="pl-landing__fw-pill">DORA</span>, title: 'DORA' },
            { node: <span className="pl-landing__fw-pill">CCPA</span>, title: 'CCPA' },
        ],
        []
    );

    return (
        <div className="pl-landing">
            <div className="pl-landing__bg" aria-hidden="true">
                {ballEnabled && (
                    <BackgroundBoundary>
                        {LOGIN_BACKGROUND === 'orb' ? (
                            <div className="pl-landing__orb">
                                <Orb colors={['#F29214', '#E6721D', '#052873']} backgroundColor="#031a4d" size={0.95} glow={1.1} />
                            </div>
                        ) : LOGIN_BACKGROUND === 'fibers' ? (
                            <div className="pl-landing__fibers">
                                <GhostFibers glowColor="#F29214" lineColor="#EAEAEA" secondaryColor="#E6721D" speed={0.5} fiberStyle="lines" glow={0.9} vignette={0.35} />
                            </div>
                        ) : (
                            <div className="pl-landing__ball">
                                <CrystalizedBall preset="ember" color="#F29214" size={0.62} particleCount={9000} speed={0.8} interactive hoverStrength={0.7} />
                            </div>
                        )}
                    </BackgroundBoundary>
                )}
            </div>
            <div className="pl-landing__veil" aria-hidden="true" />
            <div className="pl-landing__grain" aria-hidden="true" />

            <main className="pl-landing__main">
                <header className="pl-landing__nav">
                    <div className="pl-landing__brand">
                        <img className="pl-landing__logo" src={LOGO_SRC} alt="ProLense" />
                    </div>
                    <span className="pl-landing__nav-note">Enterprise GRC</span>
                </header>

                <section className="pl-landing__hero">
                    <p className="pl-landing__eyebrow">
                        <Sparkles size={14} strokeWidth={2.2} aria-hidden />
                        Governance · Risk · Compliance
                    </p>
                    <h1>One operating system for assurance programs that never stand still.</h1>
                    <p className="pl-landing__hero-lead">
                        ProLense aligns frameworks, evidence, and teams so you can run ISO, SOC 2,
                        GDPR, and PCI engagements from a single control plane - with maturity tracking
                        and advisor workflows built in.
                    </p>
                    <p className="pl-landing__hero-sub">
                        Sign in with your organization&apos;s Microsoft Entra account. Access is
                        granted through enterprise app roles - no shadow IT, no duplicate logins.
                    </p>
                    <div className="pl-landing__cta-row">
                        <button type="button" className="pl-landing__cta" onClick={onEnter}>
                            <span>Log in to Get Started</span>
                            <ArrowRight size={18} strokeWidth={2.5} aria-hidden />
                        </button>
                    </div>
                    <p className="pl-landing__cta-hint">
                        New here? Your administrator assigns ProLense roles in Entra External ID
                        before your first successful sign-in.
                    </p>
                </section>

                <section className="pl-landing__loop-section" aria-labelledby="pl-landing-frameworks-heading">
                    <h2 id="pl-landing-frameworks-heading" className="pl-landing__loop-label">
                        Frameworks &amp; regimes
                    </h2>
                    <div className="pl-landing__loop-wrap">
                        <LogoLoop
                            logos={frameworkLogos}
                            speed={38}
                            direction="left"
                            logoHeight={36}
                            gap={56}
                            hoverSpeed={0}
                            scaleOnHover
                            fadeOut
                            fadeOutColor="#04163f"
                            ariaLabel="Supported compliance frameworks and standards"
                        />
                    </div>
                </section>

                <section aria-labelledby="pl-landing-capabilities-heading">
                    <h2 id="pl-landing-capabilities-heading" className="visually-hidden">
                        Platform capabilities
                    </h2>
                    <div className="pl-landing__grid">
                        <article className="pl-landing__card">
                            <div className="pl-landing__card-icon">
                                <Shield size={20} strokeWidth={2} aria-hidden />
                            </div>
                            <h3>Live compliance index</h3>
                            <p>
                                See posture across engagements in one place: control coverage, open gaps,
                                and phase-level maturity without exporting spreadsheets.
                            </p>
                        </article>

                        <article className="pl-landing__card">
                            <div className="pl-landing__card-icon">
                                <Layers size={20} strokeWidth={2} aria-hidden />
                            </div>
                            <h3>Policy hub &amp; mappings</h3>
                            <p>
                                Navigate annexes and control families, filter by function, and trace
                                requirements to evidence - so audits answer themselves faster.
                            </p>
                        </article>

                        <article className="pl-landing__card">
                            <div className="pl-landing__card-icon">
                                <FolderLock size={20} strokeWidth={2} aria-hidden />
                            </div>
                            <h3>Evidence vault</h3>
                            <p>
                                Centralize artifacts with versioning and ownership. Keep reviewers,
                                company stakeholders, and external auditors aligned on what&apos;s current.
                            </p>
                        </article>

                        <article className="pl-landing__card">
                            <div className="pl-landing__card-icon">
                                <Users size={20} strokeWidth={2} aria-hidden />
                            </div>
                            <h3>Roles that mirror how you work</h3>
                            <p>
                                Platform admins, Lead Advisors, advisors, company SPOCs, and delegates each
                                get scoped workspaces - powered by Entra app roles and SSO you already trust.
                            </p>
                            <ul className="pl-landing__list">
                                <li>Token-based API access for secure automation</li>
                                <li>JIT user alignment when roles change in IdP</li>
                            </ul>
                        </article>
                    </div>
                </section>

                <footer className="pl-landing__footer">
                    <p>© {new Date().getFullYear()} ProLense · Enterprise GRC platform</p>
                </footer>
            </main>
        </div>
    );
};

export default LandingPage;
